import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileEdit, Lock, RefreshCw, Send, Eye, ArrowLeft, Loader2,
  CheckCircle2, AlertTriangle, Clock, PlayCircle, RotateCcw, XCircle,
} from "lucide-react";
import TipTapEditor from "@/components/TipTapEditor";
import DevTestAdminPanel from "@/components/DevTestAdminPanel";

const STATUS_TONE = {
  draft: { bg: "#F1F5F9", fg: "#475569", label: "Draft" },
  in_review: { bg: "#FEF3C7", fg: "#92400E", label: "In Review" },
  approved: { bg: "#DBEAFE", fg: "#1E40AF", label: "Approved" },
  published: { bg: "#DCFCE7", fg: "#166534", label: "Published" },
  archived: { bg: "#E2E8F0", fg: "#334155", label: "Archived" },
};

function StatusPill({ status, testid }) {
  const t = STATUS_TONE[status] || STATUS_TONE.draft;
  return (
    <span data-testid={testid}
          style={{ background: t.bg, color: t.fg,
                    padding: "3px 10px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                    textTransform: "uppercase" }}>
      {t.label}
    </span>
  );
}

function pretty(ts) { return ts ? new Date(ts).toLocaleString() : "—"; }

// ---------------------------------------------------------------------------
// Template list
// ---------------------------------------------------------------------------

function TemplateList({ templates, onOpen }) {
  const grouped = {};
  templates.forEach(t => { (grouped[t.category] ||= []).push(t); });
  return (
    <div>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "#F59E0B", fontWeight: 700, margin: "8px 0" }}>
            {cat}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {items.map(t => (
              <button key={t.key} data-testid={`tm-card-${t.key}`}
                      onClick={() => onOpen(t)}
                      style={{
                        textAlign: "left", cursor: "pointer",
                        background: "#fff", border: `1px solid ${t.is_editable ? "#E5E5E0" : "#F1F5F9"}`,
                        borderRadius: 14, padding: "14px 16px",
                        opacity: t.is_editable ? 1 : 0.75, transition: "all 0.15s ease",
                      }}
                      onMouseEnter={e => { if (t.is_editable) { e.currentTarget.style.borderColor = "#1E3A8A"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = t.is_editable ? "#E5E5E0" : "#F1F5F9"; e.currentTarget.style.transform = "none"; }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  {t.is_editable
                    ? <FileEdit size={14} color="#1E3A8A" />
                    : <Lock size={14} color="#94A3B8" />}
                  <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                                  color: t.is_editable ? "#1E3A8A" : "#94A3B8", fontWeight: 700 }}>
                    {t.is_editable ? "Editable" : "Locked"}
                  </div>
                  {t.current_version ? <StatusPill status={t.current_version.status} testid={`tm-card-status-${t.key}`} /> : null}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>{t.purpose}</div>
                <div style={{ marginTop: 8, fontSize: 11, color: "#64748B" }}>
                  {t.current_version_number
                    ? <>Currently published: <strong>v{t.current_version_number}</strong></>
                    : <>Uses code-embedded default</>}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template detail editor
// ---------------------------------------------------------------------------

function TemplateEditor({ template, onBack, onReload }) {
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewVars, setPreviewVars] = useState("{}");
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const loadVersions = useCallback(async () => {
    const { data } = await api.get(`/admin/email-templates/${template.key}/versions`);
    setVersions(data.items || []);
    if ((data.items || []).length && !selectedId) {
      const draft = data.items.find(v => v.status === "draft");
      const first = draft || data.items[0];
      setSelectedId(first.id);
      setSubject(first.subject);
      setBodyHtml(first.body_html);
      setReviewNotes(first.review_notes || "");
    }
  }, [template.key, selectedId]);

  useEffect(() => {
    // Seed prefill with a starter subject/body from the catalog's sample so
    // admins have a place to start editing.
    setPreviewVars(JSON.stringify(
      Object.fromEntries((template.variables || []).map(v => [v.name, v.example || `<${v.name}>`])),
      null, 2));
    loadVersions();
  }, [template.key, template.variables, loadVersions]);

  const selectVersion = (v) => {
    setSelectedId(v.id);
    setSubject(v.subject);
    setBodyHtml(v.body_html);
    setReviewNotes(v.review_notes || "");
  };

  const doPreview = useCallback(async () => {
    try {
      const vars = JSON.parse(previewVars || "{}");
      const { data } = await api.post("/admin/email-templates/preview",
        { subject, body_html: bodyHtml, variables: vars });
      setPreviewSubject(data.subject); setPreviewHtml(data.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview error");
    }
  }, [previewVars, subject, bodyHtml]);

  useEffect(() => {
    const t = setTimeout(doPreview, 400);   // debounce as the admin types
    return () => clearTimeout(t);   // debounce cleanup
  }, [doPreview]);

  const createNewDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${template.key}/versions`,
        { subject: subject || "Subject line", body_html: bodyHtml || "<p>Compose the body here.</p>", review_notes: reviewNotes });
      toast.success(`Draft v${data.version_number} created`);
      setSelectedId(data.id); await loadVersions(); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.patch(`/admin/email-templates/versions/${selectedId}`,
        { subject, body_html: bodyHtml, review_notes: reviewNotes });
      toast.success("Draft saved");
      await loadVersions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/versions/${selectedId}/submit`,
        { notes: reviewNotes });
      toast.success("Submitted for review");
      await loadVersions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/versions/${selectedId}/approve`, {});
      toast.success("Approved");
      await loadVersions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/versions/${selectedId}/publish`, {});
      toast.success("Published — DB version now live");
      await loadVersions(); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason) { toast.error("Provide a rejection reason."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/versions/${selectedId}/reject`,
        { reason: rejectReason });
      toast.success("Rejected — bounced to draft");
      setRejectReason("");
      await loadVersions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Rollback and republish this archived version?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/email-templates/versions/${versionId}/rollback`, {});
      toast.success("Rolled back and republished");
      await loadVersions(); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
    finally { setBusy(false); }
  };

  const testSend = async () => {
    if (!testEmail) { toast.error("Enter a recipient."); return; }
    setBusy(true);
    try {
      const vars = JSON.parse(previewVars || "{}");
      const { data } = await api.post("/admin/email-templates/test-send",
        { to_email: testEmail, subject, body_html: bodyHtml, variables: vars });
      toast.success(`Test send OK · ${data.resend_id?.slice(0, 8) || "sent"}…`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const selected = versions.find(v => v.id === selectedId);
  const status = selected?.status || "draft";
  const canEdit = status === "draft" || !selected;
  const isEditable = template.is_editable;

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} className="email-infra-btn email-infra-btn--secondary"
                data-testid="tm-back-btn">
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                        color: "#F59E0B", fontWeight: 700 }}>
            {template.category} · <code>{template.key}</code>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, color: "#0F172A" }}>{template.name}</h2>
          <p style={{ color: "#64748B", fontSize: 13, margin: "4px 0 0" }}>{template.purpose}</p>
        </div>
        {selected ? <StatusPill status={status} testid="tm-current-status" /> : null}
      </div>

      {!isEditable ? (
        <div style={{ background: "#FEF3C7", color: "#92400E", padding: 14, borderRadius: 10, marginBottom: 16 }}
             data-testid="tm-locked-banner">
          <strong>Locked — code-only template.</strong> This template accepts complex object
          variables (lists / dicts) which the current WYSIWYG editor can&apos;t safely round-trip.
          It will continue to be rendered by the code function in <code>{template.module}</code>.
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
        {/* Version list */}
        <div className="email-infra-card" style={{ height: "fit-content" }}>
          <div className="email-infra-card__title">Versions</div>
          {isEditable ? (
            <button onClick={createNewDraft} disabled={busy}
                    className="email-infra-btn email-infra-btn--primary"
                    style={{ width: "100%", justifyContent: "center", marginBottom: 8 }}
                    data-testid="tm-new-draft-btn">
              + New draft
            </button>
          ) : null}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {versions.length === 0 ? (
              <div style={{ fontSize: 12, color: "#64748B" }}>No DB versions yet.</div>
            ) : versions.map(v => (
              <button key={v.id}
                      onClick={() => selectVersion(v)}
                      data-testid={`tm-version-${v.version_number}`}
                      style={{
                        textAlign: "left", cursor: "pointer",
                        padding: "8px 10px", borderRadius: 8,
                        border: selectedId === v.id ? "2px solid #1E3A8A" : "1px solid #E5E5E0",
                        background: selectedId === v.id ? "#F0F5FF" : "#fff",
                      }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ fontSize: 13 }}>v{v.version_number}</strong>
                  <StatusPill status={v.status} />
                </div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
                  {pretty(v.updated_at)}
                </div>
                {v.status === "archived" ? (
                  <button onClick={(e) => { e.stopPropagation(); rollback(v.id); }}
                          style={{ marginTop: 6, fontSize: 11, color: "#1E3A8A",
                                  background: "transparent", border: 0, cursor: "pointer",
                                  padding: 0, textDecoration: "underline" }}
                          data-testid={`tm-rollback-v${v.version_number}`}>
                    <RotateCcw size={11} style={{ display: "inline", marginRight: 2 }} />
                    Rollback & republish
                  </button>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {/* Editor + preview */}
        <div>
          {isEditable && selected ? (
            <div className="email-infra-card">
              <div className="email-infra-card__title">Edit draft (v{selected.version_number})</div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" }}>Subject line</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} disabled={!canEdit}
                        style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #CBD5E1",
                                  marginTop: 6, fontSize: 14, background: canEdit ? "#fff" : "#F1F5F9" }}
                        data-testid="tm-subject-input" />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" }}>Body</label>
                {canEdit ? (
                  <div style={{ marginTop: 6 }}>
                    <TipTapEditor value={bodyHtml} onChange={setBodyHtml} testid="tm-editor" />
                    <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>
                      Reference variables with <code>{`{{ variable_name }}`}</code> — they&apos;ll be substituted at send time.
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 6, padding: 12, background: "#F9F8F6", borderRadius: 8,
                                  fontSize: 12, color: "#64748B" }}>
                    Body is locked because this version is <StatusPill status={status} />. Create a new draft to make edits.
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: "0.1em", textTransform: "uppercase" }}>Review notes</label>
                <textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} disabled={!canEdit}
                            rows={2}
                            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #CBD5E1", marginTop: 6, fontSize: 13, background: canEdit ? "#fff" : "#F1F5F9" }}
                            data-testid="tm-notes-input" />
              </div>
              {selected.rejection_reason ? (
                <div style={{ padding: 10, background: "#FEE2E2", color: "#991B1B", borderRadius: 8, fontSize: 12, marginBottom: 10 }}
                     data-testid="tm-rejection-banner">
                  <strong>Rejected:</strong> {selected.rejection_reason}
                </div>
              ) : null}

              {/* Workflow actions */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {canEdit ? (
                  <>
                    <button onClick={saveDraft} disabled={busy}
                            className="email-infra-btn email-infra-btn--secondary"
                            data-testid="tm-save-draft-btn">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save draft
                    </button>
                    <button onClick={submitReview} disabled={busy}
                            className="email-infra-btn email-infra-btn--primary"
                            data-testid="tm-submit-btn">
                      <Clock size={14} /> Submit for review
                    </button>
                  </>
                ) : null}
                {status === "in_review" ? (
                  <>
                    <button onClick={approve} disabled={busy}
                            className="email-infra-btn email-infra-btn--primary"
                            data-testid="tm-approve-btn">
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input placeholder="Rejection reason" value={rejectReason}
                              onChange={e => setRejectReason(e.target.value)}
                              style={{ padding: 8, borderRadius: 8, border: "1px solid #CBD5E1", fontSize: 12 }}
                              data-testid="tm-reject-reason-input" />
                      <button onClick={reject} disabled={busy || !rejectReason}
                              className="email-infra-btn email-infra-btn--secondary"
                              data-testid="tm-reject-btn">
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </>
                ) : null}
                {status === "approved" ? (
                  <button onClick={publish} disabled={busy}
                          className="email-infra-btn email-infra-btn--primary"
                          data-testid="tm-publish-btn">
                    <PlayCircle size={14} /> Publish
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Preview + test-send */}
          <div className="email-infra-card">
            <div className="email-infra-card__title"><Eye size={14} /> Live preview</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Variables (JSON)</label>
                <textarea value={previewVars} onChange={e => setPreviewVars(e.target.value)}
                            rows={10}
                            style={{ width: "100%", fontFamily: "monospace", fontSize: 12, padding: 8, marginTop: 6,
                                      borderRadius: 8, border: "1px solid #CBD5E1" }}
                            data-testid="tm-preview-vars" />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Rendered subject</label>
                <div style={{ padding: 8, background: "#F9F8F6", borderRadius: 6, fontSize: 13,
                                marginTop: 6, minHeight: 34 }} data-testid="tm-preview-subject">
                  {previewSubject}
                </div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#475569", marginTop: 8, display: "block" }}>Rendered body</label>
                <iframe title="Preview" srcDoc={previewHtml} sandbox=""
                        style={{ width: "100%", minHeight: 280, marginTop: 6, border: "1px solid #E5E5E0",
                                  borderRadius: 8, background: "#fff" }}
                        data-testid="tm-preview-iframe" />
              </div>
            </div>

            {isEditable ? (
              <div style={{ marginTop: 12, padding: 12, background: "#F0F5FF", borderRadius: 8 }}>
                <div className="email-infra-card__title" style={{ marginBottom: 6 }}>
                  <Send size={14} /> Test send this draft
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input type="email" placeholder="recipient@example.com"
                          value={testEmail} onChange={e => setTestEmail(e.target.value)}
                          style={{ minWidth: 260, padding: 8, borderRadius: 8, border: "1px solid #CBD5E1" }}
                          data-testid="tm-test-email" />
                  <button className="email-infra-btn email-infra-btn--primary"
                          onClick={testSend} disabled={busy || !testEmail}
                          data-testid="tm-test-send-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function AdminEmailTemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [params, setParams] = useSearchParams();

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates/");
      setTemplates(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const k = params.get("key");
    if (k) setOpenKey(k);
  }, [params]);

  const open = (t) => {
    if (!t.is_editable) {
      toast.info("This template is locked (complex variables). Preview only.");
    }
    setOpenKey(t.key); setParams({ key: t.key });
  };
  const back = () => { setOpenKey(null); setParams({}); };

  const openTemplate = templates.find(t => t.key === openKey);

  return (
    <div className="email-infra-shell" data-testid="admin-email-template-manager">
      <div className="email-infra-hero">
        <div className="email-infra-hero__eyebrow">Administration · Email Templates</div>
        <h1 className="email-infra-hero__title">Template Manager</h1>
        <div className="email-infra-hero__sub">
          Edit, review, approve, and publish HarborWell email templates. Dual-approval enforced —
          the person who submits or drafts a version cannot approve or publish it.
        </div>
      </div>

      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Link to="/admin/email-infrastructure" style={{ fontSize: 13, color: "#1E3A8A", textDecoration: "none" }}
                data-testid="tm-back-to-infra">
            ← Email Infrastructure Dashboard
          </Link>
        </div>
        <button className="email-infra-btn email-infra-btn--secondary" onClick={reload}
                disabled={loading} data-testid="tm-reload-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
        <button className="email-infra-btn email-infra-btn--secondary"
                onClick={() => setShowDevPanel(true)}
                data-testid="tm-open-dev-panel-btn"
                title="Provision a time-limited test admin to validate dual-approval">
          Dev · Approval Test Admin
        </button>
      </div>

      {showDevPanel ? (
        <DevTestAdminPanel onClose={() => setShowDevPanel(false)} />
      ) : null}

      {openTemplate ? (
        <TemplateEditor template={openTemplate} onBack={back} onReload={reload} />
      ) : (
        <TemplateList templates={templates} onOpen={open} />
      )}
    </div>
  );
}
